import React, {
  type ReactElement,
  type ReactNode,
  useEffect,
  useMemo,
  useReducer,
  useState,
  useContext,
} from 'react';

import { dfReducer } from './reducer';
import { initialState } from '../state';
import { DFContext } from '../df-context';
import { search } from '../../api/search';
import { getConfig } from '../../utils/config';
import { useQuery } from '@tanstack/react-query';
import { CACHE_TIME } from '../../constants';
import { SET_AVAILABLE_FILTERS, SET_SELECTED, SET_TOTAL_DOWNLOADS } from '../actions';
import { useLoader } from '../../hooks/use-loader';
import { ErrorContext } from '../../api/error-boundary';
import { useInitAttributes } from '../../hooks/use-init-attributes';
import { useAvailableFilters } from '../../hooks/use-available-filters';

export function DfSearchProvider({ children }: { children: ReactNode }): ReactElement {
  const [state, dispatch] = useReducer(dfReducer, initialState);
  const { selected, itemFilter, totalDownloads } = state;
  const errorContext = useContext(ErrorContext);
  const pimLanguage = getConfig('pim_language', 'en');
  const cmsNativeLanguage = getConfig('cms_native_language', 'EN-EN');
  const initDownloadAttributes = useInitAttributes(pimLanguage, dispatch);
  const [availableFilters] = useAvailableFilters();

  const setSelected = (depth: number, id: string | number) => {
    dispatch({
      type: SET_SELECTED,
      depth,
      id: typeof id === 'string' ? parseInt(id, 10) : id,
    });
  };

  const setTotalDownloads = (amount = 0) => {
    dispatch({
      type: SET_TOTAL_DOWNLOADS,
      totalDownloads: amount ?? 0,
    });
  };

  const { data, isLoading, error, isError } = useQuery({
    queryKey: [selected, itemFilter],
    queryFn: async () => {
      const params = Object.entries(selected).map(([depth, pimId]) => ({
        depth: parseInt(depth, 10),
        pimId: pimId,
      }));

      return await search({
        cmsNativeLanguage,
        pimLanguage,
        params,
        filterType: itemFilter?.fileType,
        filterLanguage: itemFilter?.language,
        rows: 0,
      });
    },
    staleTime: Infinity,
    gcTime: CACHE_TIME,
    retry: false,
  });

  useEffect(() => {
    if (isError) {
      errorContext?.setError(error.message);
    }
  }, [error, isError, errorContext]);

  const [newState, setNewState] = useState<any>(state);

  useEffect(() => {
    initDownloadAttributes();
  }, [initDownloadAttributes]);

  useEffect(() => {
    if (data?.data) {
      if (!totalDownloads) {
        setTotalDownloads(data.data.searchResult.numFound);
      }
      setNewState({
        selectionAmount: data.data.searchResult.numFound,
        countedCategories: data.data.categoryFilterResult.countedCategories || [],
        filterAttributes: data.data.categoryFilterResult.filter || {
          dfAttributeDfFileType: {},
          dfAttributeDfLanguage: {},
        },
      });
    }
  }, [data, totalDownloads]);

  const showLoader = useLoader(isLoading);
  const value = useMemo(
    () => ({
      state: {
        ...state,
        isLoading: showLoader,
        ...newState,
      },
      dispatch,
      setSelected,
    }),
    [newState, showLoader, state],
  );

  useEffect(() => {
    if (availableFilters) {
      dispatch({
        type: SET_AVAILABLE_FILTERS,
        value: availableFilters,
      });
    }
  }, [availableFilters]);

  return <DFContext.Provider value={value}>{children}</DFContext.Provider>;
}
